// ── Workspace Tasks page ──

import { api } from '../api.js';
import { checkAuth, getUser } from '../auth.js';
import { showToast, esc, projectSwatchColor, schemeSurfaceStyle } from '../ui.js';
import { escapeHtml, formatDate, debounce, TYPES, TYPE_LABELS, getTypeIcon } from '../utils.js';
import { initLayout, getContentEl, updateLayoutContext } from '../layout.js';
import { openTaskDetail, closeTaskDetail } from '../components/task-detail.js';
import { loadWorkspaceStatuses, statusNames, statusLabel, statusBadgeAttrs, closedNames } from '../lib/statuses.js';

// ── Constants ──

const SORT_COLUMNS = ['project_name', 'task_key', 'title', 'status', 'assignee', 'priority', 'updated_at'];

// ── State ──

let state = {};
function resetState() {
  state = {
    tasks: [], total: 0, loading: false,
    // status default is filled in once loadWorkspaceStatuses() resolves (C1182) — excludes
    // whatever names hold the complete/canceled role across the union (C1187).
    filters: { status: [], projectIds: [], assignee: '', tag: '', q: '', types: [] },
    sortCol: 'updated_at', sortDir: 'desc',
    groupBy: 'none',
    limit: 50, offset: 0,
    projects: [],
    statuses: [],
    knownTags: [],
    root: null,
    documentClickBound: false,
  };
}
resetState();

function getWorkspaceElement(id) {
  return state.root?.querySelector('#' + id) || document.getElementById(id);
}

// ── Helpers ──

// Badge color for a project (C1266) — its color_scheme swatch, or a per-id hash color
// while color_scheme is still 'default'. See projectSwatchColor() in ui.js.
function projectColor(projectId) {
  const project = state.projects.find(p => p.id === projectId);
  return projectSwatchColor(project && project.color_scheme, projectId);
}

// Inline-style fragment (C1472) carrying --proj-bg/--proj-text/--proj-muted/--proj-track
// for this row's .ws-project-badge — '' while color_scheme is still 'default'. Mirrors
// dashboard.js's projectSurface(); see schemeSurfaceStyle() in ui.js.
function projectSurface(projectId) {
  const project = state.projects.find(p => p.id === projectId);
  return schemeSurfaceStyle(project && project.color_scheme);
}

function buildQueryString() {
  const p = new URLSearchParams();
  if (state.filters.status.length) p.set('status', state.filters.status.join(','));
  if (state.filters.projectIds.length) p.set('project_id', state.filters.projectIds.join(','));
  if (state.filters.assignee) p.set('assignee', state.filters.assignee);
  if (state.filters.tag) p.set('tag', state.filters.tag);
  if (state.filters.q) p.set('q', state.filters.q);
  if (state.filters.types.length) p.set('type', state.filters.types.join(','));
  p.set('limit', state.limit);
  p.set('offset', state.offset);
  return p.toString();
}

function collectTags() {
  const set = new Set();
  state.tasks.forEach(t => (t.tags || []).forEach(tag => set.add(tag)));
  state.knownTags = [...set].sort();
}

function assigneeLabel(task) {
  if (!task.assignee) return '<span class="text-muted">—</span>';
  const me = getUser();
  if (me && task.assignee === me.id) return '<span class="ws-assignee-me">Me</span>';
  return '<span class="ws-assignee-id">#' + task.assignee + '</span>';
}

// ── Fetch ──

async function fetchTasks(append) {
  if (state.loading) return;
  state.loading = true;
  if (!append) state.offset = 0;

  try {
    const qs = buildQueryString();
    const res = await api.get('/workspace/tasks?' + qs);
    if (append) {
      state.tasks.push(...res.tasks);
    } else {
      state.tasks = res.tasks;
    }
    state.total = res.total;
    collectTags();
  } catch (err) {
    showToast(err.message || 'Failed to load tasks', 'error');
  }

  state.loading = false;
  renderTableArea();
  renderPagination();
}

async function loadWorkspaceTaskData({ projects, statuses } = {}) {
  if (Array.isArray(projects)) {
    state.projects = projects;
  } else {
    try {
      const res = await api.get('/projects');
      state.projects = res.projects || [];
    } catch (_) { /* proceed without project filter */ }
  }

  state.statuses = Array.isArray(statuses) ? statuses : await loadWorkspaceStatuses();
  const names = statusNames(state.statuses);
  const closed = new Set(closedNames(state.statuses));
  state.filters.status = names.filter(n => !closed.has(n));
}

// ── Sort & Group ──

function getSortedTasks() {
  const col = state.sortCol;
  const dir = state.sortDir === 'asc' ? 1 : -1;
  return [...state.tasks].sort((a, b) => {
    let va = a[col], vb = b[col];
    if (va == null) va = '';
    if (vb == null) vb = '';
    if (col === 'updated_at' || col === 'created_at') {
      return dir * (new Date(va) - new Date(vb));
    }
    if (col === 'assignee' || col === 'priority') {
      return dir * ((+va || 0) - (+vb || 0));
    }
    return dir * String(va).localeCompare(String(vb));
  });
}

function groupTasks(sorted) {
  if (state.groupBy === 'none') return [{ key: '_all', label: '', tasks: sorted }];

  const map = new Map();
  for (const t of sorted) {
    let key, label;
    if (state.groupBy === 'project') {
      key = t.project_id; label = t.project_name || 'Unknown';
    } else if (state.groupBy === 'status') {
      key = t.status; label = statusLabel(t.status);
    } else if (state.groupBy === 'assignee') {
      key = t.assignee || 0;
      const me = getUser();
      label = !t.assignee ? 'Unassigned' : (me && t.assignee === me.id ? 'Me' : '#' + t.assignee);
    }
    if (!map.has(key)) map.set(key, { key, label, tasks: [] });
    map.get(key).tasks.push(t);
  }
  return [...map.values()];
}

// ── Render ──

function renderRow(t) {
  const color = projectColor(t.project_id);
  const tags = (t.tags || []).slice(0, 3);
  const extraTags = (t.tags || []).length - 3;
  return `<tr class="ws-row" data-task-key="${esc(t.task_key)}" data-project-id="${t.project_id}">
    <td><span class="ws-project-badge" style="--proj-color:${color}${projectSurface(t.project_id)}">${esc(t.project_name)}</span></td>
    <td class="ws-cell-key"><a class="task-key-link" href="#/projects/${t.project_id}/tasks/${encodeURIComponent(t.task_key)}" draggable="false" title="Open task page">${esc(t.task_key)}</a></td>
    <td class="ws-cell-title">${esc(t.title)}</td>
    <td><span class="badge ${statusBadgeAttrs(state.statuses.find(s => s.name === t.status)).className}" style="${statusBadgeAttrs(state.statuses.find(s => s.name === t.status)).style}">${esc(statusLabel(t.status))}</span></td>
    <td>${assigneeLabel(t)}</td>
    <td class="ws-cell-sprint">P${t.priority}</td>
    <td>${tags.map(tg => `<span class="tag">${esc(tg)}</span>`).join('')}${extraTags > 0 ? `<span class="text-muted text-sm">+${extraTags}</span>` : ''}</td>
    <td class="text-muted ws-cell-date">${formatDate(t.updated_at)}</td>
  </tr>`;
}

function renderTheadRow() {
  const cols = [
    { key: 'project_name', label: 'Project' },
    { key: 'task_key', label: 'Key' },
    { key: 'title', label: 'Title' },
    { key: 'status', label: 'Status' },
    { key: 'assignee', label: 'Member Assignee' },
    { key: 'priority', label: 'Sprint' },
    { key: null, label: 'Tags' },
    { key: 'updated_at', label: 'Updated' },
  ];
  return '<tr>' + cols.map(c => {
    if (!c.key) return `<th>${c.label}</th>`;
    const sorted = state.sortCol === c.key;
    const cls = ['ws-th-sortable'];
    if (sorted) cls.push('ws-sorted', 'ws-sorted-' + state.sortDir);
    return `<th class="${cls.join(' ')}" data-col="${c.key}">${c.label}</th>`;
  }).join('') + '</tr>';
}

function renderTableArea() {
  const area = getWorkspaceElement('ws-table-area');
  if (!area) return;

  if (state.loading) {
    area.innerHTML = '<div class="empty"><span class="spinner"></span></div>';
    return;
  }
  if (!state.tasks.length) {
    area.innerHTML = '<div class="empty text-muted">No tasks found</div>';
    return;
  }

  const sorted = getSortedTasks();
  const groups = groupTasks(sorted);

  if (state.groupBy === 'none') {
    area.innerHTML = `<table class="ws-table"><thead>${renderTheadRow()}</thead><tbody>${sorted.map(renderRow).join('')}</tbody></table>`;
  } else {
    area.innerHTML = groups.map(g => `
      <div class="ws-group">
        <div class="ws-group-header">
          <span class="ws-group-title">${esc(g.label)}</span>
          <span class="ws-group-count badge">${g.tasks.length}</span>
        </div>
        <table class="ws-table"><thead>${renderTheadRow()}</thead><tbody>${g.tasks.map(renderRow).join('')}</tbody></table>
      </div>`).join('');
  }

  bindTableEvents();
}

function renderPagination() {
  const el = getWorkspaceElement('ws-pagination');
  if (!el) return;
  const shown = state.tasks.length;
  const total = state.total;
  if (!shown) { el.innerHTML = ''; return; }

  el.innerHTML = `<div class="ws-pagination">
    <span class="text-muted">Showing ${shown} of ${total} tasks</span>
    ${shown < total ? '<button class="btn btn-ghost btn-sm" id="ws-load-more">Load More</button>' : ''}
  </div>`;

  const btn = document.getElementById('ws-load-more');
  if (btn) btn.addEventListener('click', () => { state.offset += state.limit; fetchTasks(true); });
}

function renderFilterBar() {
  return `<div class="ws-filter-bar" id="ws-filter-bar">
    <div class="ws-filter-group">
      <button class="btn btn-ghost btn-sm" id="ws-project-filter">Projects <span style="font-size:0.7rem">▾</span></button>
      <div class="ws-dropdown" id="ws-project-dropdown">
        ${state.projects.map(p => `<label><input type="checkbox" value="${p.id}" ${state.filters.projectIds.includes(p.id) ? 'checked' : ''}> ${esc(p.name)}</label>`).join('')}
        ${!state.projects.length ? '<div class="text-muted text-sm" style="padding:0.5rem">No projects</div>' : ''}
      </div>
    </div>
    <div class="bf-types" id="ws-type-filters">
      ${TYPES.map(t => {
        const isActive = state.filters.types.length > 0 && state.filters.types.includes(t);
        return `<button class="bf-type-btn${isActive ? ' bf-type-active' : ''}" data-type="${t}" title="${TYPE_LABELS[t]}">${getTypeIcon(t, 16)}</button>`;
      }).join('')}
    </div>
    <div class="ws-filter-group ws-status-filters" id="ws-status-filters">
      ${statusNames(state.statuses).map(s => `<label><input type="checkbox" value="${esc(s)}" ${state.filters.status.includes(s) ? 'checked' : ''}> ${esc(statusLabel(s))}</label>`).join('')}
    </div>
    <select class="form-select form-select-sm" id="ws-assignee-filter">
      <option value="">All Members</option>
      <option value="me" ${state.filters.assignee === 'me' ? 'selected' : ''}>Assigned to Me</option>
    </select>
    <div class="ws-filter-group ws-tag-filter-wrap">
      ${state.filters.tag
        ? `<span class="ws-active-tag"><span class="tag">${esc(state.filters.tag)}</span><button class="btn btn-ghost btn-sm ws-tag-clear" style="padding:0 4px;font-size:0.8rem">×</button></span>`
        : `<input type="text" class="form-input form-input-sm" id="ws-tag-input" placeholder="Filter by tag..." style="width:140px">`
      }
      <div class="ws-tag-suggestions" id="ws-tag-suggestions"></div>
    </div>
    <input type="text" class="form-input form-input-sm ws-search" id="ws-search" placeholder="Search tasks..." value="${esc(state.filters.q)}">
  </div>`;
}

// Reusable All Tasks content. The caller owns the surrounding page and chooses whether
// to include the standalone page header (C1276).
export function renderWorkspaceTaskList({ includeHeader = true } = {}) {
  return `${includeHeader ? `
    <div class="page-header" style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:0.75rem">
      <h2>All Tasks</h2>
      <div class="ws-controls">
        <select class="form-select form-select-sm" id="ws-group-by">
          <option value="none">No grouping</option>
          <option value="project">By Project</option>
          <option value="status">By Status</option>
          <option value="assignee">By Member Assignee</option>
        </select>
      </div>
    </div>` : ''}
    ${renderFilterBar()}
    <div id="ws-table-area"><div class="empty"><span class="spinner"></span></div></div>
    <div id="ws-pagination"></div>`;
}

// ── Event binding ──

function bindFilterEvents() {
  const bar = getWorkspaceElement('ws-filter-bar');
  if (!bar) return;

  // Type toggles
  const typeFilters = getWorkspaceElement('ws-type-filters');
  if (typeFilters) {
    typeFilters.querySelectorAll('.bf-type-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const type = btn.dataset.type;
        if (state.filters.types.includes(type)) {
          state.filters.types = state.filters.types.filter(t => t !== type);
        } else {
          state.filters.types = [...state.filters.types, type];
        }
        if (state.filters.types.length === TYPES.length) state.filters.types = [];
        reRenderFilterBar();
        fetchTasks(false);
      });
    });
  }

  // Status checkboxes
  const statusFilters = getWorkspaceElement('ws-status-filters');
  if (statusFilters) {
    statusFilters.addEventListener('change', () => {
      state.filters.status = [...statusFilters.querySelectorAll('input:checked')].map(cb => cb.value);
      fetchTasks(false);
    });
  }

  // Project dropdown toggle
  const projBtn = getWorkspaceElement('ws-project-filter');
  const projDrop = getWorkspaceElement('ws-project-dropdown');
  if (projBtn && projDrop) {
    projBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      projDrop.classList.toggle('open');
    });
    projDrop.addEventListener('change', () => {
      state.filters.projectIds = [...projDrop.querySelectorAll('input:checked')].map(cb => +cb.value);
      fetchTasks(false);
    });
    if (!state.documentClickBound) {
      const closeProjectDropdown = (e) => {
        const currentBtn = getWorkspaceElement('ws-project-filter');
        const currentDrop = getWorkspaceElement('ws-project-dropdown');
        if (currentDrop && !currentDrop.contains(e.target) && e.target !== currentBtn) {
          currentDrop.classList.remove('open');
        }
      };
      document.addEventListener('click', closeProjectDropdown);
      state.documentClickBound = true;
      state.cleanupDocumentClick = () => document.removeEventListener('click', closeProjectDropdown);
    }
  }

  // Assignee
  const assigneeSel = getWorkspaceElement('ws-assignee-filter');
  if (assigneeSel) {
    assigneeSel.addEventListener('change', () => {
      state.filters.assignee = assigneeSel.value;
      fetchTasks(false);
    });
  }

  // Tag input
  bindTagInput();

  // Tag clear
  const tagClear = bar.querySelector('.ws-tag-clear');
  if (tagClear) {
    tagClear.addEventListener('click', () => {
      state.filters.tag = '';
      reRenderFilterBar();
      fetchTasks(false);
    });
  }

  // Search
  const searchInput = document.getElementById('ws-search');
  if (searchInput) {
    const debouncedSearch = debounce(() => {
      state.filters.q = searchInput.value.trim();
      fetchTasks(false);
    }, 300);
    searchInput.addEventListener('input', debouncedSearch);
  }

  // Group-by
  const groupSel = document.getElementById('ws-group-by');
  if (groupSel) {
    groupSel.addEventListener('change', () => {
      state.groupBy = groupSel.value;
      renderTableArea();
    });
  }
}

function bindTagInput() {
  const input = getWorkspaceElement('ws-tag-input');
  const suggestions = getWorkspaceElement('ws-tag-suggestions');
  if (!input || !suggestions) return;

  input.addEventListener('input', debounce(() => {
    const val = input.value.trim().toLowerCase();
    if (!val) { suggestions.innerHTML = ''; return; }
    const matches = state.knownTags.filter(t => t.toLowerCase().includes(val)).slice(0, 8);
    suggestions.innerHTML = matches.map(t => `<div class="ws-tag-suggestion" data-tag="${esc(t)}">${esc(t)}</div>`).join('');
  }, 150));

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && input.value.trim()) {
      e.preventDefault();
      state.filters.tag = input.value.trim();
      reRenderFilterBar();
      fetchTasks(false);
    }
  });

  suggestions.addEventListener('click', (e) => {
    const item = e.target.closest('.ws-tag-suggestion');
    if (item) {
      state.filters.tag = item.dataset.tag;
      reRenderFilterBar();
      fetchTasks(false);
    }
  });
}

function reRenderFilterBar() {
  const bar = getWorkspaceElement('ws-filter-bar');
  if (!bar) return;
  bar.outerHTML = renderFilterBar();
  bindFilterEvents();
}

function bindTableEvents() {
  const area = getWorkspaceElement('ws-table-area');
  if (!area) return;

  // Sort
  area.querySelectorAll('.ws-th-sortable').forEach(th => {
    th.addEventListener('click', () => {
      const col = th.dataset.col;
      if (state.sortCol === col) {
        state.sortDir = state.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        state.sortCol = col;
        state.sortDir = 'asc';
      }
      renderTableArea();
    });
  });

  // Row click → task detail
  area.querySelectorAll('.ws-row').forEach(row => {
    row.addEventListener('click', (e) => {
      if (e.target.closest('a')) return;
      const projectId = row.dataset.projectId;
      const taskKey = row.dataset.taskKey;
      openTaskDetail(projectId, taskKey, {
        allTasks: state.tasks,
        onClose: () => fetchTasks(false),
      });
    });
  });
}

function cleanupWorkspaceTaskList() {
  closeTaskDetail();
  if (state.cleanupDocumentClick) state.cleanupDocumentClick();
  resetState();
}

// Mount reusable All Tasks content into a page-owned container. Dashboard passes its
// already-loaded project/status registries to avoid duplicate metadata requests.
export async function mountWorkspaceTaskList(container, options = {}) {
  resetState();
  state.root = typeof container === 'string' ? document.querySelector(container) : container;
  if (!state.root) return () => {};

  await loadWorkspaceTaskData(options);
  state.root.innerHTML = renderWorkspaceTaskList(options);
  bindFilterEvents();
  await fetchTasks(false);

  return cleanupWorkspaceTaskList;
}

// ── Page entry ──

export async function workspaceTasksPage() {
  if (!(await checkAuth())) return;
  initLayout(getUser());
  updateLayoutContext({ projectId: null, projectName: null, activeView: 'tasks', viewLabel: 'All Tasks' });

  return mountWorkspaceTaskList(getContentEl());
}
